import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import apiService from '../../services/apiServices';

// Async thunk for fetching all sales
export const fetchSales = createAsyncThunk('sales/fetchSales', async (params) => {
    const response = await apiService.get('sales-invoices', { params });
    return response.data;
});

export const searchSales = createAsyncThunk(
    'sales/searchSales',
    async (query) => {
        const response = await apiService.get(`sales-invoices/search?q=${query}`);
        return response.data;
    }
);



export const fetchSalesById = createAsyncThunk('sales/updateSale', async (id) => {
    const response = await apiService.get(`sales-invoices/${id}`);
    return response.data;
});

// Async thunk for creating a new sale
export const createSale = createAsyncThunk('sales/createSale', async (saleData) => {
    const response = await apiService.post('sales-invoices', saleData);
    return response.data;
});

// Async thunk for updating a sale
export const updateSale = createAsyncThunk('sales/updateSale', async ({ id, saleData }) => {
    const response = await apiService.put(`sales-invoices/${id}`, saleData);
    return response.data;
});

// Async thunk for deleting a sale
export const deleteSale = createAsyncThunk('sales/deleteSale', async (id) => {
    const response = await apiService.delete(`sales-invoices/${id}`);
    return response.data; // Return the id to remove it from the state
});


// Async thunk for processing payment
export const processPayment = createAsyncThunk('sales/processPayment', async ({ invoiceId, paymentDetails }) => {
    const response = await apiService.post(`sales-invoices/${invoiceId}/pay`, paymentDetails);
    return response.data;
});

// Fetch due sales for a customer
export const fetchDueSalesForCustomerId = createAsyncThunk(
    'sales/fetchDueSalesForCustomerId',
    async (id) => {
        const response = await apiService.get(`sales-invoices/due/customer/${id}`);
        return response.data;
    }
);

const salesSlice = createSlice({
    name: 'sales',
    initialState: {
        sales: [],
        customerDueSales: [],
        totalRecords: 0,
        draftSales: [],
        loading: false,
        error: null,
        paymentSuccess: false,
    },
    reducers: {
        resetPaymentSuccess: (state) => {
            state.paymentSuccess = false;
        },
        addDraftSale: (state, action) => {
            state.draftSales.push(action.payload);
        },
        removeDraftSale: (state, action) => {
            state.draftSales = state.draftSales.filter(draftSale => draftSale.id !== action.payload.id);
        },
        updateDraftSale: (state, action) => {
            const index = state.draftSales.findIndex(draftSale => draftSale.id === action.payload.id);
            if (index !== -1) {
                state.draftSales[index] = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSales.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSales.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload && action.payload.docs) {
                    state.sales = action.payload.docs;
                    state.totalRecords = action.payload.totalRecords;
                } else {
                    state.sales = action.payload;
                    state.totalRecords = action.payload ? action.payload.length : 0;
                }
            })
            .addCase(fetchSales.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(createSale.pending, (state) => {
                state.loading = true;
            })
            .addCase(createSale.fulfilled, (state, action) => {
                state.loading = false;
                state.sales.push(action.payload);
            })
            .addCase(createSale.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(updateSale.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateSale.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.sales.findIndex(sale => sale._id === action.payload._id);
                if (index !== -1) {
                    state.sales[index] = action.payload;
                }
            })
            .addCase(updateSale.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(deleteSale.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteSale.fulfilled, (state, action) => {
                state.loading = false;
                state.sales = state.sales.filter(sale => sale._id !== action.payload);
            })
            .addCase(deleteSale.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(processPayment.pending, (state) => {
                state.loading = true;
            })
            .addCase(processPayment.fulfilled, (state) => {
                state.loading = false;
                state.paymentSuccess = true; // Indicate payment was successful
            })
            .addCase(processPayment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(fetchDueSalesForCustomerId.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchDueSalesForCustomerId.fulfilled, (state, action) => {
                state.loading = false;
                state.customerDueSales = action.payload;
            })
            .addCase(fetchDueSalesForCustomerId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    },
});
export const { addDraftSale, removeDraftSale, updateDraftSale } = salesSlice.actions
export const { resetPaymentSuccess } = salesSlice.actions;

export default salesSlice.reducer;